import { Feather, TreePine, Mountain, Quote, MapPin } from 'lucide-react';
import { useState } from 'react';
import { HOME } from '@/constants/testIds';

// Preferred photo — the actual Highland spot that inspired the badge.
// Drop your real photo at /app/frontend/public/brand/ben-lomond.jpg and it
// will replace the fallback automatically.
const HIGHLAND_IMG = '/brand/ben-lomond.jpg';
const HIGHLAND_FALLBACK =
  'https://images.unsplash.com/photo-1609674750700-33895b9b7ce1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxzY290dGlzaCUyMG1vdW50YWluc3xlbnwwfHx8fDE3ODE1MTY5NDh8MA&ixlib=rb-4.1.0&q=85&w=1400';

export default function About() {
  const [imgSrc, setImgSrc] = useState(HIGHLAND_IMG);
  const usingFallback = imgSrc === HIGHLAND_FALLBACK;

  return (
    <section
      id="about"
      data-testid={HOME.aboutSection}
      className="section relative"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Header */}
          <div className="lg:col-span-5 flex flex-col justify-end">
            <span className="font-eyebrow">The Estate Mark · Passed down</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--brand-forest)] leading-[1.02]">
              A Gamekeeper, an heirloom, and{' '}
              <span className="italic">a working life</span> on the hill.
            </h2>
            <p className="mt-6 text-[var(--brand-muted)] text-lg leading-relaxed max-w-md">
              It began with a Gamekeeper’s heirloom from the Marquis of Bute, gifted to my great-great-grandfather
              for his years keeping the Ayrshire estate. My grandfather is its custodian now,
              and passes it down the family to whoever will make it earn its keep. My first real paycheck
              was in Gamekeeping and Estate maintenance &mdash; my Dad gave me the shooting bug from
              I was a boy. Still on the hill today &mdash; only the transport has changed.
            </p>

            {/* Founder bio block */}
            <div
              data-testid="founder-bio"
              className="mt-8 border-l-2 border-[var(--brand-gold)] pl-5 max-w-md text-[var(--brand-ink)]"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--brand-olive)] font-semibold mb-3">
                Why a Gamekeeper picks the kit himself
              </div>
              <p className="text-base leading-relaxed">
                Waterproofs that leak in the second season are junk. Boots that shed a lug
                on a scree slope are dangerous. A stove that won&rsquo;t light in wet wind is a
                night of regret. I&rsquo;ve seen every version of that story since I was a
                boy on the hill.
              </p>
              <p className="mt-3 text-base leading-relaxed">
                So when I chose what to stock &mdash; the bikes, the tents, the billy cans, the
                sporks &mdash; I only picked things I&rsquo;d trust myself, on the same ground
                that gave me my first paycheck as a Gamekeeper. Ruggedness first. Respect for the
                ground you&rsquo;re on second. Nothing else needs saying.
              </p>
              <p className="mt-3 text-base leading-relaxed">
                No marketing department. No warehouse. No nonsense. Just one Gamekeeper picking
                gear that survives the Highlands, and shipping it on direct from the maker.
              </p>
              <p className="mt-4 text-sm font-semibold text-[var(--brand-forest)]">
                &mdash; The Founder · Former Gamekeeper · Perthshire
              </p>
            </div>
          </div>

          {/* Bento grid */}
          <div className="lg:col-span-7 grid grid-cols-6 grid-rows-6 gap-4 min-h-[560px]">
            {/* Big image — the Highland spot that inspired the badge.
                Loads /brand/ben-lomond.jpg if present, else falls back to a
                stock Scottish mountain until the founder drops in the real file. */}
            <div className="bento col-span-6 sm:col-span-4 row-span-4 relative overflow-hidden">
              <img
                src={imgSrc}
                onError={() => {
                  if (imgSrc !== HIGHLAND_FALLBACK) setImgSrc(HIGHLAND_FALLBACK);
                }}
                alt="The Highland spot that inspired the EcoBike Camping Supplies Co. badge"
                data-testid="about-highland-image"
                className="absolute inset-0 w-full h-full object-cover editorial-img"
              />

              {/* Brass pin — top-right of the image */}
              <div
                data-testid="highland-location-pin"
                className="absolute top-4 right-4 inline-flex items-center gap-2 bg-[var(--brand-forest-deep)]/85 backdrop-blur-sm border border-[var(--brand-gold)]/70 text-[var(--brand-parchment)] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.20em]"
              >
                <MapPin size={12} className="text-[var(--brand-gold)]" strokeWidth={2} />
                {usingFallback ? 'The place the badge was born' : 'Ben Lomond · from the west'}
              </div>

              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[var(--brand-forest-deep)]/90 to-transparent p-6 text-[var(--brand-parchment)]">
                <Quote size={20} className="text-[var(--brand-gold)]" />
                <p className="font-display italic text-xl mt-2 leading-snug max-w-md">
                  &ldquo;The hills don&rsquo;t care how new your kit is. Only whether it lasts the walk home.&rdquo;
                </p>
                <p className="mt-3 text-xs uppercase tracking-[0.22em] opacity-80">
                  — The Founder · A working life on the hill
                </p>
              </div>
            </div>

            {/* Pine */}
            <div className="bento olive col-span-3 sm:col-span-2 row-span-2 p-6 flex flex-col justify-between">
              <TreePine size={28} strokeWidth={1.4} />
              <div>
                <div className="font-display text-2xl leading-tight">Pine &amp; Oak</div>
                <p className="text-sm opacity-90 mt-1">
                  British hardwood — the same timber the estates have always used, for gates, gunstocks and beat boxes.
                </p>
              </div>
            </div>

            {/* Feather pheasant */}
            <div className="bento dark col-span-3 sm:col-span-2 row-span-2 p-6 flex flex-col justify-between">
              <Feather size={28} strokeWidth={1.4} className="text-[var(--brand-gold)]" />
              <div>
                <div className="font-display text-2xl leading-tight">Estate Down</div>
                <p className="text-sm opacity-90 mt-1">
                  Ethically-sourced fill, fully traceable, never mulesed. Warmth that lasts a Highland decade.
                </p>
              </div>
            </div>

            {/* Corbett */}
            <div className="bento col-span-6 sm:col-span-4 row-span-2 p-6 flex items-center gap-5">
              <Mountain size={36} strokeWidth={1.3} className="text-[var(--brand-olive)] shrink-0" />
              <div>
                <div className="font-display text-2xl text-[var(--brand-forest)] leading-tight">
                  Learned in the field. Passed on.
                </div>
                <p className="text-sm text-[var(--brand-muted)] mt-1">
                  Every product we stock has been chosen for the next generation of outdoors folk — kit that lasts a lifetime, not a season.
                </p>
              </div>
            </div>

            {/* Numbers */}
            <div className="bento col-span-3 sm:col-span-2 row-span-2 p-6 flex flex-col justify-between">
              <span className="font-eyebrow">Estate Way</span>
              <div className="font-display text-5xl text-[var(--brand-forest)]">
                Repair
              </div>
              <p className="text-sm text-[var(--brand-muted)]">
                Rebuilt, not replaced. Free re-waxing &amp; tune-ups on every flagship piece.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
